<script setup>
    import { computed, ref, watchEffect } from 'vue'
    import InputHeader from '@/js/components/Common/InputHeader.vue'
    import LehrFormen from '@/js/components/Modul/LehrFormen.vue'
    import LehrFormenEdit from '@/js/components/Modul/LehrFormenEdit.vue'
    import { TextareaField } from '@/js/components/Common'
    import { useEditStateStore } from '@/js/stores/editState'

    const props = defineProps({
        lehrFormen: {
            type: Array,
            required: true
        },
        allowEdit: {
            type: Boolean,
            required: false,
            default: false
        },
        tooltip: {
            type: String,
            required: false,
            default: () => ''
        },
        language: {
            type: String,
            required: false,
            default: () => 'deutsch'
        }
    })

    const title = computed(() =>
        props.language === 'englisch' ? 'Type of instruction | Modulstruktur' : 'Modulstruktur'
    )

    const emit = defineEmits(['lehrformenSaved'])

    const isLoading = computed(() => store.getters['isLoading'])

    // each object needs to be destructured/cloned deep to avoid mutating the props
    const editLehrFormen = ref([])
    const editStore = useEditStateStore()
    const editKey = 'modulstruktur'
    editStore.register([editKey])
    const isEditing = computed(() => editStore.editStates[editKey])

    function startEditing() {
        editStore.set(editKey, true)
    }

    function stopEditing() {
        editStore.set(editKey, false)
    }

    const workloadSum = computed(() => {
        return props.lehrFormen.reduce(
            (sumWorkload, lehrform) => sumWorkload + Number(lehrform.gesamt.replace(',', '.')),
            0
        )
    })

    const formatWorkload = (workload) => Number(workload) + ' h'

    const resetEditLehrFormen = () => {
        editLehrFormen.value = props.lehrFormen.map((lehrform) => JSON.parse(JSON.stringify(lehrform)))
    }

    watchEffect(() => {
        editLehrFormen.value = props.lehrFormen.map((lehrform) => JSON.parse(JSON.stringify(lehrform)))
    })

    const handleEditClick = () => {
        if (isLoading.value) {
            return
        }
        resetEditLehrFormen()
        startEditing()
    }

    const handleAbortClick = () => {
        if (isLoading.value) {
            return
        }
        resetEditLehrFormen()
        stopEditing()
    }

    const saveLehrformen = () => {
        emit('lehrformenSaved', editLehrFormen.value)
        stopEditing()
    }
</script>

<template>
    <v-form novalidate @submit.prevent>
        <div class="d-flex w-100 flex-nowrap">
            <input-header :title="title" :tool-tip="tooltip" />
            <div class="w-100">
                <div class="d-flex flex-column flex-nowrap pa-2">
                    <div class="d-flex flex-nowrap">
                        <textarea-field
                            class="bg-primary-lighten-2 font-weight-bold text-grey lehrform-header"
                            :model-value="language === 'englisch' ? 'Type of course | Lernform' : 'Lernform'"
                            readonly
                        />
                        <textarea-field
                            class="bg-primary-lighten-2 font-weight-bold text-grey lehrform-header"
                            :model-value="language === 'englisch' ? 'Contact hrs | Kontaktzeit' : 'Kontaktzeit'"
                            readonly
                        />
                        <textarea-field
                            class="bg-primary-lighten-2 font-weight-bold text-grey text-grey lehrform-header"
                            :model-value="
                                language === 'englisch' ? 'Independent study | Selbststudium' : 'Selbststudium'
                            "
                            readonly
                        />
                        <textarea-field
                            class="bg-primary-lighten-2 font-weight-bold text-grey lehrform-header"
                            :model-value="language === 'englisch' ? 'Total | Gesamt' : 'Gesamt'"
                            readonly
                        />
                        <v-btn v-if="isEditing" height="inherit" readonly icon="mdi-check" class="opacity-0" />
                    </div>
                    <lehr-formen v-if="!isEditing" :lehr-formen="lehrFormen" />
                    <lehr-formen-edit v-if="isEditing" v-model:lehrformen="editLehrFormen" />
                    <div class="align-self-end mt-1 d-flex align-stretch align-center">
                        <div
                            class="pa-3 font-weight-bold bg-primary-lighten-2 border border-primary border-opacity-100 text-grey"
                            readonly
                        >
                            Workload:
                        </div>
                        <div
                            class="pa-3 bg-primary-lighten-2 border border-primary border-opacity-100 text-grey"
                            readonly
                        >
                            {{ formatWorkload(workloadSum) }}
                        </div>
                        <v-btn v-if="isEditing" icon="mdi-clear" class="opacity-0 cursor-auto">
                            invisible content
                        </v-btn>
                    </div>
                </div>
            </div>
            <v-btn
                v-if="allowEdit && !isEditing"
                icon="mdi-lead-pencil"
                border
                height="inherit"
                class="border-primary border-opacity-100 text-grey"
                color="primary-lighten-2"
                tile
                variant="flat"
                @click="handleEditClick"
            />
            <v-btn
                v-if="allowEdit && isEditing"
                icon="mdi-check"
                border
                height="inherit"
                class="border-primary border-opacity-100 text-grey"
                color="primary-lighten-2"
                tile
                variant="flat"
                @click="saveLehrformen"
            />
            <v-btn
                v-if="allowEdit && isEditing"
                icon="mdi-cancel"
                border
                height="inherit"
                class="border-primary border-opacity-100 text-grey"
                color="primary-lighten-2"
                tile
                variant="flat"
                @click="handleAbortClick"
            />
        </div>
    </v-form>
</template>

<script></script>

<style scoped lang="scss">
    label {
        margin-bottom: 0 !important;
    }

    .input-entry-show {
        white-space: pre-line;
    }

    .lehrform-header > input {
        color: black;
        font-weight: 200;
    }
</style>
